import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useSocketScreen } from '@/composables/domains/chat/useSocketScreen';
import { useChatPreferences } from '@/composables/shared/useChatPreferences';
import { useSocketStore } from '@/stores/socket';
import { LanguageCode } from '@/types/common';
import {
  ChatMessage,
  IMessageCn,
  IMessageFc,
  MessageAl,
  MessageCa,
  MessageCc,
  MessageCd,
  MessageEm,
  MessageHistory,
  MessageMap,
  MessageText,
  MessageTr,
} from '@/types/socket';
import { InboundMessageDM } from '@/types/socket/inbound';
import { isLanguageNoSpace } from '@/utils/chat/language';
import { base64UrlSafeToString, stringToBase64UrlSafe } from '@/utils/encryptor.js';
import { SocketState } from '@/utils/enums';

export function useMessagesScreen() {
  const { locale } = useI18n();
  const socketStore = useSocketStore();
  const { updateScreenPreferences } = useChatPreferences();

  const { connectToWebsocket } = useSocketScreen(ref(socketStore.selectedChatId));
  const messageMap = ref<MessageMap>({}); // 수신된 메세지 모듬 정제 1단계
  const messageList = ref<Array<ChatMessage>>([]); // 수신된 메세지 모듬 정제 2단계
  const messageBank = ref<any>([]);
  const prevOtherMessageId = ref('');

  let intervalRetryConnection = 0;
  let dateMessageLast = '';

  // 1. 웹 소켓 메세지 처리
  const handleMessageWebSocket = ({ data }) => {
    messageBank.value.push(data); // 디버깅 용도
    const flag = data.slice(0, 2);
    console.log(`INBOUND WS ${flag} ${JSON.stringify(data).trim().slice(0, 100)}`);

    // Early return for ignored flags
    switch (flag) {
      case 'pi':
        return;
      case 'td':
        socketStore.recover = true;
        return;
      case 'nd':
        socketStore.close();
        localStorage.setItem('accessAuthentication', '0');
        return;
    }

    // Ensure message is parsed properly
    const message = parseWebSocketMessage(data);
    if (!message) return;

    switch (flag) {
      case 'cn':
        handleMessageCn(message);
        break;
      case 'cc':
        handleMessageCc(message);
        return;
      case 'cd':
        return handleMessageCd(message);
      case 'ca':
        return handleMessageCa(message);
      case 'at':
        handleMessageAt(message);
        return;
      case 'al':
        handleMessageAl(message);
        return;
      case 'am':
        return;
      case 'em':
        handleMessageEm(message);
        return;
      case 'fi':
        socketStore.isFastInterpretation = message.isFastInterpretationOn === '1';
        return;
      case 'ts':
        handleMessageTs(message);
        break;
      case 'tr':
        handleMessageTr(message);
        break;
      case 'fc':
        return handleMessageFc(message);
      case 'dm':
        handleMessageDm(message);
        break;
      case 'er':
        switch (String(message.code || message.errorCode)) {
          case '2002':
            console.debug('No audio header!!');
            socketStore.socketState = SocketState.ERROR_NO_HEADER;
            break;
          case '1023':
            // 호스트가 없을 경우 5초마다 소켓 재연결
            if (!intervalRetryConnection) intervalRetryConnection = setInterval(() => connectToWebsocket(), 5000);
        }
        return;
      default:
        return;
    }
    if (message.isAudioComplete && !messageMap.value[message.callLogId]) {
      console.debug('오디오 파일만 있는 빈 버블 현상을 방지했습니다');
      return; // 글자 없이 오디오 파일만 받은 경우
    }
  };

  // 2. 웹소켓 메시지 정제
  const parseWebSocketMessage = (data: any) => {
    try {
      if (data instanceof ArrayBuffer) {
        data = new TextDecoder().decode(data);
      }
      if (typeof data !== 'string') return null;

      const flag = data.slice(0, 2);
      const length = parseInt(data.slice(2, 10), 10);
      if (data.length < 10 + length) throw new Error('Data length mismatch');

      return JSON.parse(data.substring(10));
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
      return null;
    }
  };

  // 소켓 연결 성공 메세지
  const handleMessageCa = (message: MessageCa) => {
    clearInterval(intervalRetryConnection);
    localStorage.setItem('userIdConnected', localStorage.getItem('userId') ?? '');
    socketStore.selectedChatId = message.chatId;
    socketStore.connectionResponse = 2;
    socketStore.socketState = SocketState.OPEN;
  };

  // 참석자 입장 메세지
  const handleMessageCc = ({ userId, callLogId, nickname, isPartnerPerson, sentAt }: MessageCc) => {
    const nicknameParsed = nickname ? base64UrlSafeToString(nickname) : '';
    initializeMessageMap(callLogId, nicknameParsed, userId, sentAt, '0');
    socketStore.addAttendeeByUserId(userId, nicknameParsed, isPartnerPerson);
    socketStore.sendMessageAttendees();
  };

  // 참석자 관련 메세지
  const handleMessageAl = (message: MessageAl) => {
    socketStore.setAttendees(message.attendees);
    // 닉네임이 없는 메세지가 하나라도 있으면 발신자 아이디값 기준으로 매핑
    const messageMapEntries = Object.entries(messageMap.value);
    messageMapEntries.forEach(([callLogId, msgData]) => {
      if (msgData.nickname || !msgData.senderId) return;
      const nicknameNew = socketStore.findAttendeeNickname(msgData.senderId);
      if (nicknameNew) {
        msgData.nickname = nicknameNew;
        updateMessageList(callLogId, 'tr');
      }
    });
  };

  // 메세지 수정 메세지
  const handleMessageEm = (messageEm: MessageEm) => {
    const { callLogId, subId, message, languageCode } = messageEm;

    const indexBubble = messageList.value.findIndex((msg) => {
      const tgtLangSet = msg.tgtLanguageSet;
      const srcLangSet = msg.srcLanguageSet;

      return msg.callLogId === callLogId && (tgtLangSet.has(languageCode) || srcLangSet.has(languageCode));
    });
    if (indexBubble === -1) return;

    const chatMessage = messageList.value[indexBubble];
    const { tgtLanguageSet } = chatMessage;

    const tgtHasLang = tgtLanguageSet.has(languageCode);

    const changeTarget = tgtHasLang ? 'tgt' : 'src';
    const indexSubSeq = chatMessage[changeTarget].contents.findIndex(
      (content: MessageText) => content.chatSeqSubId == subId
    );
    if (indexSubSeq === -1) {
      return;
    }
    const target = chatMessage[changeTarget].contents[indexSubSeq];

    messageMap.value[callLogId][`${changeTarget}History`]![subId].messageEdited = message;
    updateContentsFromHistory(callLogId, changeTarget, target.logFlag!, languageCode);
    updateMessageList(callLogId, 'tr');
  };

  // 연결 거부
  const handleMessageCd = (message: MessageCd) => {
    if (message.userId !== sessionStorage.getItem('userId')) return true;
    if (message.reasonCode === '0') return false;
    if (message.reasonCode === '1') {
      localStorage.setItem('accessAuthentication', '0');
      socketStore.evicted = true;
      socketStore.close();

      return true;
    }
    if (message.reasonCode === '2') {
      socketStore.evicted = true;
    }
    if (message.reasonCode === '3') {
      socketStore.evicted = true;
    }
  };

  const handleMessageAt = ({ count }) => {
    if (socketStore.attendees.size !== count) {
      socketStore.sendMessageAttendees();
    }
  };

  // 입력중
  const handleMessageTs = (message) => {
    prevMyMessageId.value = message.callLogId;
  };

  // 통역 업데이트
  const handleMessageTr = async (messageTr: MessageTr) => {
    const {
      chatId,
      callLogId,
      isAudioComplete,
      dataFlag,
      isTextComplete,
      message,
      sentAt,
      senderId,
      reset,
      chatSeqSubId,
      logFlag,
      isComplete,
      isFastInterpretationOn,
      languageCode,
      voiceKey,
      incompleteText,
    } = messageTr;
    const indexMessage = messageList.value.findIndex((msg) => msg.callLogId === callLogId);

    if (isAudioComplete && !messageMap.value[callLogId]) {
      console.debug('오디오 파일만 있는 빈 버블 현상을 방지했습니다');
      return; // 글자 없이 오디오 파일만 받은 경우
    }

    let nicknameDecoded = socketStore.findAttendeeNickname(senderId ?? '');

    if (indexMessage !== -1) {
      messageList.value[indexMessage].isAudioComplete = isAudioComplete;
      // 오디오 수신 완료 시 showLoading 강제 False 처리
      if (isAudioComplete && messageList.value[indexMessage].showLoading) {
        messageList.value[indexMessage].showLoading = false;
        messageList.value[indexMessage].reset = '1';
      }

      if (!nicknameDecoded) {
        nicknameDecoded = messageList.value[indexMessage].nickname;
      }
    }
    if (dataFlag === 'src') {
      prevOtherMessageId.value = callLogId;
    }

    initializeMessageMap(callLogId, nicknameDecoded, senderId ?? '', sentAt, reset);

    // 이미 완료된 메세지는 더 어상 수정하지 않음
    if (
      messageMap.value[callLogId]?.[`${dataFlag}History`]?.[chatSeqSubId]?.thoroughlyCompletedContent?.length > 0 &&
      !voiceKey
    ) {
      console.error('앞의 내용이 누락되지 않고 이 메세지가 없다면 해결 완료:', message);
      return;
    }

    if (!isAudioComplete) {
      console.log(
        `[TEXT][${dataFlag}] id: ${callLogId} ${chatSeqSubId} ${logFlag}, text/isComplete: ${isTextComplete} ${isComplete} (${isFastInterpretationOn}) ${senderId}, timestamp : ${new Date().toLocaleString()}, msg: ${message}, inCompleteText: ${incompleteText}`
      );
    } else if (isAudioComplete) {
      console.log(
        `[AUDIO] ${dataFlag} callLogId: ${callLogId}, subId: ${chatSeqSubId}, completed:${isTextComplete}/${isAudioComplete}, msg: ${message}, vUrl: ${voiceKey}`
      );
    }

    if (sentAt <= 0) return;

    if (!['src', 'tgt'].includes(dataFlag)) return;

    const historyKey = `${dataFlag}History`;
    const languageSetKey = `${dataFlag}LanguageSet`;

    messageMap.value[callLogId][dataFlag] ??= {};
    messageMap.value[callLogId][historyKey] ??= {};
    messageMap.value[callLogId][languageSetKey] ??= new Set();
    messageMap.value[callLogId][languageSetKey].add(languageCode);
    messageMap.value[callLogId][dataFlag].isComplete = isComplete;
    messageMap.value[callLogId][dataFlag].isAudioComplete = isAudioComplete;
    messageMap.value[callLogId].nickname = nicknameDecoded;

    if (isAudioComplete) {
      messageMap.value[callLogId][dataFlag] ??= {};
      messageMap.value[callLogId].isAudioComplete = true;
      const existingAudioKeys = messageMap.value[callLogId][dataFlag]?.audioKey ?? [];
      const newAudioEntry = voiceKey
        ? {
            chatId: chatId,
            chatSeqSubId: chatSeqSubId,
            voiceKey: voiceKey,
            callLogId: callLogId,
            logFlag: logFlag,
          }
        : null;

      messageMap.value[callLogId][dataFlag].audioKey ??= existingAudioKeys;
      if (newAudioEntry) {
        messageMap.value[callLogId][dataFlag].audioKey.push(newAudioEntry);
      }

      updateMessageList(callLogId, 'tr');

      return;
    } else messageMap.value[callLogId].isAudioComplete = false;

    // 동시통역
    switch (isFastInterpretationOn) {
      case '1': // 켜짐
        handleMessageFiOn(messageTr, historyKey);
        break;
      case '0': // 꺼짐
        handleMessageFiOff(messageTr, historyKey);
        break;
    }

    updateMessageList(callLogId, 'tr');
  };

  // 닉네임 변경
  const handleMessageCn = (messageCn: IMessageCn) => {
    socketStore.setAttendees(
      [...socketStore.attendees].map((attendee) => ({
        ...attendee,
        nickname: attendee.userId === messageCn.userId ? messageCn.nickname : stringToBase64UrlSafe(attendee.nickname),
      }))
    );

    if (messageCn.isAll) {
      messageList.value.forEach((msg) => {
        if (msg.senderId === messageCn.userId) {
          msg.nickname = base64UrlSafeToString(messageCn.nickname);
        }
      });
    }
  };

  // 사용자 설정값 강제변경
  const handleMessageFc = (messageFc: IMessageFc) => {
    if (!messageFc.screenAttribute) {
      return;
    }
    updateScreenPreferences(messageFc.screenAttribute);
    socketStore.sendMessageChangeLanguage(messageFc.screenAttribute.languageCode);
  };

  const handleMessageDm = (msg: InboundMessageDM) => {
    const inx = messageList.value.findIndex(({ callLogId }) => callLogId === msg.chatLogId);
    if (inx !== -1) {
      messageList.value.splice(inx, 1);
    }
  };

  // 빠른 통역
  const handleMessageFiOn = (messageTr: MessageTr, historyKey: string) => {
    const {
      callLogId,
      chatSeqSubId,
      message,
      incompleteText,
      isTextComplete,
      isComplete,
      logFlag,
      dataFlag,
      languageCode,
    } = messageTr;

    const history = (messageMap.value[callLogId][historyKey][chatSeqSubId] ??= {
      completedContents: [],
      creatingContent: null,
    });
    const newMessage = { message, logFlag };

    if (isTextComplete || isComplete) {
      if (isComplete && message.trim() === '' && !history.creatingContent) {
        return;
      }

      if (isComplete) {
        history.thoroughlyCompletedContent = [...history.completedContents, newMessage];
        history.key = Date.now();
        history.completedContents = [];
        history.creatingContent = null;
      } else {
        history.completedContents.push(newMessage);
      }

      history.creatingContent = {
        message: incompleteText,
        logFlag,
      };
    } else {
      if (!history.thoroughlyCompletedContent) {
        history.creatingContent = newMessage;
      }
    }

    messageMap.value[callLogId][dataFlag] ??= {};
    updateContentsFromHistory(callLogId, dataFlag, logFlag, languageCode);
  };

  // 확정 통역
  const handleMessageFiOff = (messageTr: MessageTr, historyKey: string) => {
    const { callLogId, chatSeqSubId, message, isComplete = false, dataFlag, logFlag, languageCode } = messageTr;

    const history = (messageMap.value[callLogId][historyKey][chatSeqSubId] ??= {
      completedContents: [],
      content: '',
      isTextCompleted: false,
      key: Date.now(),
    });

    if (isComplete) {
      history.completedContents = [...history.completedContents, { message, logFlag }];
      history.creatingContent = null;
    } else {
      history.creatingContent = { message, logFlag };
    }

    messageMap.value[callLogId][dataFlag] ??= {};
    updateContentsFromHistory(callLogId, dataFlag, logFlag, languageCode);
  };

  // 메세지 맵 초기화
  const initializeMessageMap = (
    callLogId: string,
    nickname: string,
    senderId: string,
    sentAt: number,
    reset: '0' | '1'
  ) => {
    if (!messageMap.value[callLogId]) {
      messageMap.value[callLogId] = {
        src: { contents: [], isComplete: false },
        srcHistory: {},
        srcLanguageSet: new Set(),
        tgt: { contents: [], isComplete: false },
        tgtHistory: {},
        tgtLanguageSet: new Set(),
        sentAt: sentAt ?? new Date(),
        senderId: senderId,
        nickname: nickname,
        reset: reset,
        timer: 0,
      };
    }
  };

  // 축적된 history를 메세지에 담기
  const updateContentsFromHistory = (
    callLogId: string,
    dataFlag: string,
    logFlag: string,
    languageCode: LanguageCode
  ) => {
    const historyKey = `${dataFlag}History`;
    messageMap.value[callLogId][dataFlag].contents = Object.entries(
      messageMap.value[callLogId][historyKey] as Record<string, MessageHistory>
    )
      .flatMap(([key, value], index, array) => {
        const isLast = index === array.length - 1;
        const isFirst = index === 0;
        const result: Array<MessageText> = [];

        const isNoSpaceLang = isLanguageNoSpace(languageCode);
        const splitter = isNoSpaceLang ? '' : ' ';

        if (value.thoroughlyCompletedContent) {
          let contentParsed = value.thoroughlyCompletedContent.map((content) => content.message).join(splitter);
          if (isNoSpaceLang) contentParsed = contentParsed.replaceAll(' ', '');
          if (!isFirst) contentParsed = splitter + contentParsed;

          result.push({
            highlight: false,
            content: contentParsed,
            fadeout: true,
            key: value.key,
            isThoroughlyComplete: true,
            messageEdited: value.messageEdited,
            chatSeqSubId: key,
            isEditable: true,
            logFlag,
            languageCode,
          });
        } else if (value.completedContents?.length > 0) {
          let contentParsed = value.completedContents.map((content) => content.message).join(splitter);
          if (!isFirst) contentParsed = splitter + contentParsed;

          result.push({
            highlight: false,
            content: contentParsed,
            fadeout: isLast,
            key: Date.now(),
            isThoroughlyComplete: false,
            messageEdited: value.messageEdited,
            chatSeqSubId: key,
            // isEditable: true,
            logFlag,
            languageCode,
          });
        }

        if (value.creatingContent?.message?.trim?.()) {
          result.push({
            highlight: true,
            content: `${splitter}${value.creatingContent.message}`,
            fadeout: false,
            key: Date.now(),
            isThoroughlyComplete: false,
            messageEdited: value.messageEdited,
            chatSeqSubId: key,
            logFlag,
            languageCode,
          });
        }

        return result;
      })
      .sort((a, b) => parseInt(a.chatSeqSubId ?? '0') - parseInt(b.chatSeqSubId ?? '0'));
  };

  const getMessageContents = (data) => {
    const messageContents = { src: {}, tgt: {} };

    if (data.src) {
      const { contents = [], isComplete = false, isAudioComplete = false } = data.src;
      messageContents.src = {
        contents: contents.filter(({ content = '' }) => content),
        isComplete,
        isAudioComplete,
      };
    }

    if (data.tgt) {
      const { contents = [], isComplete = false, isAudioComplete = false } = data.tgt;
      messageContents.tgt = {
        contents: contents.filter(({ content = '' }) => content),
        isComplete,
        isAudioComplete,
      };
    }

    return messageContents;
  };

  // 메세지 배열 갱신
  const updateMessageList = (messageId: string, flag?: any): void => {
    if (!messageMap.value[messageId]) return;
    const data = messageMap.value[messageId];

    const { src, tgt } = getMessageContents(data);

    const message: ChatMessage = {
      callLogId: messageId,
      src,
      tgt,
      srcAudioKeys: data.src?.audioKey ?? [],
      tgtAudioKeys: data.tgt?.audioKey ?? [],
      sentAt: data.sentAt,
      inout: data.inout,
      isOwn: data.senderId === localStorage.getItem('userIdConnected'),
      time: new Date(data.sentAt).toLocaleTimeString(locale.value.replace('_', '-'), {
        hour: '2-digit',
        minute: '2-digit',
      }),
      senderId: (data.senderId || data.userId) as string,
      nickname: data.nickname,
      autoPlay: true,
      flag: flag,
      reset: data.reset as '0' | '1',
      srcLanguageSet: data.srcLanguageSet!,
      tgtLanguageSet: data.tgtLanguageSet!,
      isAudioComplete: data.isAudioComplete ?? false,
    };

    const index = messageList.value.findIndex((msg) => msg.callLogId === messageId);

    if (index !== -1) {
      messageList.value[index] = { ...messageList.value[index], ...message };
    } else {
      addMessageDate(data.sentAt);
      messageList.value.push(message);
    }

    messageList.value = [...messageList.value]; // force reactivity
  };

  // 날짜 메세지 추가
  const addMessageDate = (sentAt?: number) => {
    const dateMessageCurrent = sentAt ? new Date(sentAt).toDateString() : new Date().toDateString();

    if (dateMessageLast !== dateMessageCurrent) {
      const dateMessage: ChatMessage = {
        callLogId: '', // Unique ID for the date message
        src: { contents: [] },
        tgt: { contents: [] },
        srcAudioKeys: [],
        tgtAudioKeys: [],
        sentAt: sentAt ?? Date.now(),
        inout: '',
        isOwn: false,
        time: '',
        nickname: '',
        autoPlay: false,
        flag: 'date',
        srcLanguageSet: new Set(),
        tgtLanguageSet: new Set(),
      };

      messageList.value.push(dateMessage);
      dateMessageLast = dateMessageCurrent;
    }
  };

  const resetScreenMessages = () => {
    messageMap.value = {};
    messageList.value = [];
    messageBank.value = [];
    prevOtherMessageId.value = '';
    dateMessageLast = '';
  };

  return {
    messageMap,
    messageList,
    handleMessageWebSocket,
    resetScreenMessages,
  };
}
